import React, { useState, useEffect } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useLocation } from 'react-router-dom';
import { HouseDoor, PersonAdd, ClipboardPlus, ListUl, BoxArrowRight, BoxSeam, Rocket, GraphUp, MoonFill, SunFill, PlusCircle } from 'react-bootstrap-icons';
import axios from 'axios';
import GlobalSearch from '../components/GlobalSearch';
import './navbar.css';

function BasicExample() {
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(() => {
    return parseInt(localStorage.getItem('selectedTenant')) || 1;
  });
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const isStockPage = location.pathname === '/stock';

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    if (newMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  };

  // Apply dark mode on mount
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, []);

  //Handle logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedTenant');
    window.location.href = '#/login';
  }

  // Fetch tenants on component mount and update user data
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await axios.get('/api/tenants');
        setTenants(response.data);
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    };

    // Update user data from localStorage
    const updateUserData = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
          console.error('Error parsing user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    fetchTenants();
    updateUserData();

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        updateUserData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle tenant selection
  const handleTenantChange = (tenantId) => {
    setSelectedTenant(tenantId);
    localStorage.setItem('selectedTenant', tenantId.toString());
    // Optionally reload the page to refresh data with new tenant
    window.location.reload();
  };

  if (!localStorage.getItem('token')) {
    window.location.href = '#/login';
    return null;
  }

  return (
    <Navbar expand="lg" className="modern-navbar" sticky="top">
      <Container fluid>
        <Navbar.Brand href="/" className="brand-logo">
          <div className="brand-title">Asset Hub <Rocket className="brand-icon" /></div>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" className="navbar-toggler-custom" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto nav-links">
            <Nav.Link href="/#/" className="nav-link-custom">
              <GraphUp className="nav-icon" />
              Dashboard
            </Nav.Link>
            <Nav.Link href="/#/stock" className="nav-link-custom">
              <BoxSeam className="nav-icon" />
              Stock
            </Nav.Link>
            {!isStockPage && (
              <NavDropdown
                title={
                  <span className="add-dropdown-title">
                    <PlusCircle className="nav-icon" />
                    Add
                  </span>
                }
                id="add-dropdown"
                className="add-dropdown"
              >
                <NavDropdown.Item href="/#/inv" className="add-dropdown-item">
                  <BoxSeam className="me-2" />
                  Add Equipment
                </NavDropdown.Item>
                <NavDropdown.Item href="/#/addsignout" className="add-dropdown-item">
                  <ClipboardPlus className="me-2" />
                  Add Signout
                </NavDropdown.Item>
                <NavDropdown.Item href="/#/AddAgent" className="add-dropdown-item">
                  <PersonAdd className="me-2" />
                  Add Staff
                </NavDropdown.Item>
              </NavDropdown>
            )}
            <Nav.Link href="/#/listsignouts" className="nav-link-custom">
              <ListUl className="nav-icon" />
              List Signouts
            </Nav.Link>
          </Nav>
          <div className="d-flex align-items-center gap-3">
            <GlobalSearch />
          </div>
          <Nav className="ms-auto">
            <NavDropdown
              title={
                <span className="tenant-info">
                  <span className="tenant-icon">🏢</span>
                  {tenants.find(t => t.id === selectedTenant)?.name || 'Loading...'}
                </span>
              }
              id="tenant-dropdown"
              align="end"
              className="tenant-dropdown"
            >
              {tenants.map(tenant => (
                <NavDropdown.Item
                  key={tenant.id}
                  onClick={() => handleTenantChange(tenant.id)}
                  active={tenant.id === selectedTenant}
                  className="tenant-item"
                >
                  {tenant.name}
                </NavDropdown.Item>
              ))}
            </NavDropdown>
            <NavDropdown
              title={
                <span className="user-info">
                  <span className="user-avatar">👤</span>
                </span>
              }
              id="user-dropdown"
              align="end"
            >
              <NavDropdown.Item disabled className="dropdown-info">
                Signed in as: <strong>{user?.name || user?.username || 'User'}</strong>
              </NavDropdown.Item>
              <NavDropdown.Item disabled className="dropdown-info">
                Role: <strong>{user?.role || 'user'}</strong>
              </NavDropdown.Item>
              <NavDropdown.Divider />

              <NavDropdown.Item onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDarkMode();
              }} className="theme-toggle-item">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span>Theme</span>
                  <div className="theme-switch">
                    {darkMode ? <MoonFill className="theme-icon" /> : <SunFill className="theme-icon" />}
                    <span className="ms-2">{darkMode ? 'Dark' : 'Light'}</span>
                  </div>
                </div>
              </NavDropdown.Item>

              {user?.role === 'admin' && (
                <>
                  <NavDropdown.Divider />
                  <NavDropdown.Item href="/#/admin">
                    <span className="admin-icon">⚙️</span>
                    Admin Panel
                  </NavDropdown.Item>
                </>
              )}
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout} className="logout-item">
                <BoxArrowRight className="logout-icon" />
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default BasicExample;
